import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  createOrGetContact,
  getContact,
  getConversations,
  ChatwootContact,
  ChatwootConversation,
  chatwootWebSocket,
  getUnreadCount,
  WebSocketEvent,
  ChatwootMessage,
  isUserMessage,
  sortConversationsByRecent,
  setCurrentContact,
  fetchSourceIdFromBackend,
  persistSourceIdToBackend,
} from '../../services/chatwootService';

interface UserDetails {
  user_guid?: string;
  user_id?: string;
  display_name?: string;
  name?: string;
  email?: string;
  mobile?: string;
  org_guid?: string;
  org_id?: string;
  org_domain?: string;
  roles?: string[];
  chatwoot_source_id?: string;
}

interface ChatwootContextType {
  contact: ChatwootContact | null;
  conversations: ChatwootConversation[];
  totalUnread: number;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  refreshConversations: () => Promise<void>;
}

interface ChatwootProviderProps {
  children: React.ReactNode;
  userDetails: UserDetails | null | undefined;
}

const ChatwootContext = createContext<ChatwootContextType>({
  contact: null,
  conversations: [],
  totalUnread: 0,
  isConnected: false,
  isLoading: true,
  error: null,
  refreshConversations: async () => {},
});

export const useChatwoot = (): ChatwootContextType => useContext(ChatwootContext);

export const ChatwootProvider: React.FC<ChatwootProviderProps> = ({ children, userDetails }) => {
  const [contact, setContact] = useState<ChatwootContact | null>(null);
  const [conversations, setConversations] = useState<ChatwootConversation[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isInitialized = useRef(false);
  const isLoadingRef = useRef(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleNewMessageRef = useRef<((event: WebSocketEvent) => void) | null>(null);
  const handleStatusChangeRef = useRef<(() => void) | null>(null);
  const resolvedUserGuid = userDetails?.user_guid || userDetails?.user_id;

  const totalUnread = conversations.reduce((sum, conv) => sum + getUnreadCount(conv), 0);

  const refreshConversations = useCallback(async (): Promise<void> => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    try {
      const result = await getConversations();
      if (result.success && result.data) {
        const sorted = sortConversationsByRecent(result.data);
        setConversations(sorted);
      }
    } catch (error) {
      console.error('ChatwootProvider: Failed to refresh conversations', error);
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Wait until we have a user identifier
    if (!resolvedUserGuid) {
      setContact(null);
      setConversations([]);
      setIsConnected(false);
      setIsLoading(false);
      return;
    }

    // Prevent double-initialization on re-renders
    if (isInitialized.current) return;
    isInitialized.current = true;

    const init = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const name = userDetails?.display_name || userDetails?.name || 'App User';

        // ── Step 1: GET /api/support/chatwoot/source-id ────────────────
        console.log('[Chatwoot] GET /api/support/chatwoot/source-id');
        const fetchResult = await fetchSourceIdFromBackend();
        let contact: ChatwootContact | null = null;

        if (fetchResult.status === 'found') {
          // source-id exists → load the Chatwoot contact
          console.log('[Chatwoot] source-id found:', fetchResult.sourceId, '— loading contact');
          const result = await getContact(fetchResult.sourceId);
          if (result.success && result.data) {
            contact = result.data;
          } else {
            // source-id in backend but contact missing in Chatwoot — treat as new
            console.warn('[Chatwoot] Contact not found for source-id, will create new');
          }
        } else if (fetchResult.status === 'not_found') {
          // ── Step 2: 404 → create Chatwoot contact + POST source-id ───
          console.log('[Chatwoot] source-id not found (404) — creating new Chatwoot contact');
          const createResult = await createOrGetContact({
            identifier: resolvedUserGuid,
            name,
            email: userDetails?.email ?? '',
            phone_number: userDetails?.mobile ?? '',
          });

          if (!createResult.success || !createResult.data) {
            throw new Error(createResult.error ?? 'Failed to create Chatwoot contact');
          }

          contact = createResult.data;
          console.log('[Chatwoot] Contact created, source_id:', contact.source_id);

          // POST /api/support/chatwoot/source-id
          console.log('[Chatwoot] POST /api/support/chatwoot/source-id');
          await persistSourceIdToBackend(contact.source_id);
        } else {
          // GET failed with a non-404 error — do not create source_id
          console.warn('[Chatwoot] GET source-id failed with an error — skipping contact creation');
          throw new Error('Failed to retrieve source_id from backend');
        }

        if (!contact) {
          throw new Error('Could not resolve Chatwoot contact');
        }

        setCurrentContact(contact);
        setContact(contact);

        // Load conversations
        const convResult = await getConversations();
        if (convResult.success && convResult.data) {
          setConversations(sortConversationsByRecent(convResult.data));
        }

        // Connect WebSocket
        if (contact.pubsub_token) {
          chatwootWebSocket.connect(contact.pubsub_token);
          setTimeout(() => setIsConnected(chatwootWebSocket.isConnected()), 1000);
        }

        console.log('[Chatwoot] ✅ Initialization complete');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Chatwoot initialization failed';
        console.error('[Chatwoot] ❌', msg);
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    };

    void init();

    return (): void => {
      isInitialized.current = false;
    };
  }, [resolvedUserGuid]); // re-run only when the user identity changes

  useEffect(() => {
    console.log('ChatwootProvider: Setting up WebSocket listeners');

    if (handleNewMessageRef.current) {
      chatwootWebSocket.off('message.created', handleNewMessageRef.current);
    }
    if (handleStatusChangeRef.current) {
      chatwootWebSocket.off('conversation.status_changed', handleStatusChangeRef.current);
      chatwootWebSocket.off('conversation.created', handleStatusChangeRef.current);
    }

    handleNewMessageRef.current = (event: WebSocketEvent): void => {
      console.log('ChatwootProvider: Received message.created event');
      const newMsg = event.data as unknown as ChatwootMessage;

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        void refreshConversations();
      }, 1000);

      if (!isUserMessage(newMsg)) {
        console.log('ChatwootProvider: New message from agent');
      }
    };

    handleStatusChangeRef.current = (): void => {
      console.log('ChatwootProvider: Received status change event');
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        void refreshConversations();
      }, 1000);
    };

    chatwootWebSocket.on('message.created', handleNewMessageRef.current);
    chatwootWebSocket.on('conversation.status_changed', handleStatusChangeRef.current);
    chatwootWebSocket.on('conversation.created', handleStatusChangeRef.current);

    return (): void => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (handleNewMessageRef.current) {
        chatwootWebSocket.off('message.created', handleNewMessageRef.current);
      }
      if (handleStatusChangeRef.current) {
        chatwootWebSocket.off('conversation.status_changed', handleStatusChangeRef.current);
        chatwootWebSocket.off('conversation.created', handleStatusChangeRef.current);
      }
    };
  }, [refreshConversations]);

  // Monitor WebSocket connection status
  useEffect(() => {
    console.log('ChatwootProvider: Setting up connection status monitor');
    
    // Check connection status periodically
    const connectionCheckInterval = setInterval(() => {
      const connected = chatwootWebSocket.isConnected();
      setIsConnected(connected);
      
      if (!connected) {
        console.log('⚠️ ChatwootProvider: WebSocket disconnected, isConnected state updated to false');
      }
    }, 5000); // Check every 5 seconds

    return (): void => {
      clearInterval(connectionCheckInterval);
    };
  }, []);

  return (
    <ChatwootContext.Provider 
      value={{ 
        contact, 
        conversations, 
        totalUnread, 
        isConnected,
        isLoading,
        error,
        refreshConversations 
      }}
    >
      {children}
    </ChatwootContext.Provider>
  );
};

export default ChatwootProvider;
